<template>
  <div>
    <title-bar :title-stack="titleStack" />
    <hero-bar>
      Transactions
      <div slot="right" class="navbar-message button-gap-right-aligned">
        <div class="hero-navbar-right-flex">
          <multiselect
            :disabled="switchCustomerViewEnabled"
            :value="customers.id"
            v-model="customer"
            :options="customers"
            placeholder="Please select a customer"
            label="name"
            track-by="name"
            :searchable="true"
            @input="changeCustomer($event)"
          ></multiselect>

          <b-button
            type="is-primary"
            class="import-button"
            @click="routeToImportForm"
            v-if="isEditAllowed"
          >
            Import Transactions Data</b-button
          >
        </div>
      </div>
    </hero-bar>
    <section class="section is-main-section">
      <section class="has-listing-table">
        <div class="level no-margin" v-if="customer">
          <div class="level-left bulk-actions">
            <span class="navbar-item">
              <a
                @click.prevent="additionalBulkCheck('exportTransactions')"
                data-cy="pd-bulk-edit"
                >Export</a
              >
            </span>
            <span
              class="navbar-item"
              v-if="
                !switchCustomerViewEnabled &&
                  !isViewOnlyUser &&
                  customerEntity === 'ERP UK'
              "
            >
              <a
                @click.prevent="isManualMatchingModal = true"
                data-cy="pd-manual-matching"
                >Manual Matching</a
              >
            </span>
            <span
              class="navbar-item"
              v-if="
                !switchCustomerViewEnabled &&
                  !isViewOnlyUser &&
                  customerEntity == 'ERP UK'
              "
            >
              <a @click.prevent="handleUnmatch()" data-cy="pd-manual-unmatch"
                >Unmatch</a
              >
            </span>
            <span
              class="navbar-item"
              v-if="
                (this.userExternalCheck() || this.switchCustomerView) &&
                  !isViewOnlyUser
              "
            >
              <a @click.prevent="BulkComments()" data-cy="pd-bulk-comment"
                >Comment</a
              >
            </span>
          </div>

          <!-- Column Configuration Dropdown -->
          <div class="level-right">
            <b-dropdown position="is-bottom-left">
              <template #trigger>
                <a class="navbar-item navbar-mdi-tune-dropdown">
                  <button class="button" data-cy="transactions-config-button">
                    <span class="mdi mdi-table-cog mdi-24px mdi-dark"></span>
                  </button>
                </a>
              </template>
              <form>
                <div class="filter-config-modal-body">
                  <section class="filter-config-list">
                    <span>
                      <b-field label="Column Configuration"></b-field>
                    </span>
                    <div style="padding-top: 4px 0;">
                      <b-checkbox v-model="selectAll" @input="toggleSelectAll">
                        <strong>Select All</strong>
                      </b-checkbox>
                    </div>
                    <div
                      v-for="(column, index) in columns"
                      :key="index"
                      class="control"
                    >
                      <div v-if="column.label != ''">
                        <b-dropdown-item
                          aria-role="list"
                          :focusable="false"
                          custom
                          paddingless
                        >
                          <b-checkbox
                            @input="saveFieldConfig"
                            v-model="column.visible"
                            :disabled="column.field === 'actions'"
                          >
                            {{ column.label || column.field }}
                          </b-checkbox>
                        </b-dropdown-item>
                      </div>
                    </div>
                  </section>
                </div>
              </form>
            </b-dropdown>
          </div>
        </div>
        <div class="enable-select-text">
          <circul8-table
            :tableName="'Transactions'"
            ref="circul8Table"
            @clickcheck="checkCheckBox"
            @checkedAll="checkedAllData"
            :checkedRows="checkedRow"
            :records="tableData"
            :fields="columns.filter(column => column.visible !== false)"
            :usePropRecords="true"
            :listingComponent="true"
            :isCheckable="true"
            :maxTextLength="'24'"
            :isLoading="isDataLoading"
            @querySet="searchByFilter"
            :backendSortingEnabled="true"
            @backendSort="sortData"
            :showEditButton="false"
            :enableSwitchAction="false"
            :routeNext="false"
            :showViewButton="false"
            @loadNextPage="pullNextPage"
            :enableInfiniteScroll="true"
            :hasNextData="nextData"
            :hasPreviousData="previousData"
            @loadPreviousPage="pullPreviousPage"
          />
          <bulk-transaction-export
            @confirm="viewConfirmExport"
            @cancel="viewCancel"
            :is-active="isBulkExportModal"
            @totalRecords="this.totalRecords"
          >
          </bulk-transaction-export>
          <bulk-comment
            :is-active="isBulkCommentModal"
            @confirm="saveBulkComment"
            @cancel="cancelBulkComment"
          />
          <div class="records-displayed" v-if="totalRecords > 0">
            <span v-if="isCheckedAll"> All selected, </span>
            <span v-if="checkedData.length > 0 && !isCheckedAll"
              >{{ checkedData.length }} selected,
            </span>
            <span v-if="currentPage * 50 + 50 < totalRecords"
              >displaying {{ currentPage > 1 ? currentPage * 50 - 50 : 0 }} -
              {{
                currentPage > 0
                  ? nextPageCursor
                    ? currentPage * 50 - 50 + tableData.length
                    : totalRecords
                  : tableData.length
              }}
              records out of {{ totalRecords }} records</span
            >
            <div v-else>
              <span v-if="currentPage == 0">
                displaying 0 - {{ totalRecords }} records out of
                {{ totalRecords }} records
              </span>
              <span v-else>
                displaying {{ currentPage * 50 - 50 }} -
                {{ totalRecords }} records out of {{ totalRecords }} records
              </span>
            </div>
          </div>
        </div>
        <manualMatching
          ref="manualMatchingModal"
          :is-active="isManualMatchingModal"
          :customerId="customer.id"
          @cancel="manualMatchingCancel()"
          @matchedData="saveMatchedData"
          @dataMatched="handleDataMatched"
        >
        </manualMatching>
      </section>
      <b-loading
        :is-full-page="true"
        v-model="isLoading"
        :can-cancel="false"
      ></b-loading>
    </section>
  </div>
</template>

<script>
import TitleBar from "@/components/TitleBar";
import HeroBar from "@/components/HeroBar";
import Multiselect from "vue-multiselect";
import CustomerProfileDataService from "../../services/CustomerProfileDataService";
import Circul8Table from "../../components/Circul8Table.vue";
import ManualMatching from "@/components/DataManagement/ManualMatching.vue";
import CDFTransactionDataService from "../../services/CDFTransactionDataService";
import DataManagementService from "../../services/DataManagementImportService";
import UtilityMixin from "@/mixins/UtilityMixin";
import UserPermissionMixin from "@/mixins/UserPermissionMixin";
import BulkTransactionExport from "@/components/DataManagement/BulkTransactionExport.vue";
import BulkComment from "@/components/DataManagement/BulkComment.vue";
import { FieldType } from "../../constants/FieldType";
import * as dayjs from "dayjs";
import { UserGroup } from "@/services/UserGroups";
import { mapGetters } from "vuex";
let screenPixelSize;
let pixelRatio;
export default {
  name: "DataManagementTransactions",
  mixins: [UtilityMixin, UserPermissionMixin],
  components: {
    HeroBar,
    TitleBar,
    Multiselect,
    Circul8Table,
    BulkTransactionExport,
    BulkComment,
    ManualMatching
  },
  data() {
    return {
      checkedData: [],
      checkedRow: [],
      map1: new Map(),
      isCheckedAll: false,
      checkedDataMap: new Map(),
      nextPageCursor: "",
      PreviousPageCursor: "",
      loadMoreClicked: false,
      lastSortColumn: null,
      lastSortOrder: null,
      enableLoadMore: false,
      totalRecords: 0,
      isLoading: false,
      nextData: false,
      previousData: false,
      currentPage: 0,
      customerEntity: "",
      query: "",
      isDataLoading: false,
      isBulkExportModal: false,
      customer: "",
      form: [],
      customers: [],
      cdf_Transaction_sheet: [],
      columnsForConfig: [],
      selectAll: true,
      customerDataService: new CustomerProfileDataService(),
      CDFTransactionDataService: new CDFTransactionDataService(),
      DataManagementService: new DataManagementService(),
      columns: [],
      tableData: [],
      subHeight: 315,
      switchCustomerId: null,
      switchCustomerViewEnabled: false,
      isBulkCommentModal: false,
      isManualMatchingModal: false,
      exportLargeCount: 65000
    };
  },
  watch: {
    columns: {
      handler(newColumns) {
        // Update selectAll checkbox when columns change
        this.selectAll = newColumns.every(
          column => column.field === "actions" || column.visible !== false
        );
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    ...mapGetters([
      "userId",
      "userName",
      "userHasPermission",
      "userGroups",
      "mappedCustomer",
      "switchCustomerView"
    ]),
    isEditAllowed() {
      if (
        this.userGroups.includes(UserGroup.EPR_ADMIN) //||
        // this.userGroups.includes(UserGroup.REPORTING_RESPONSIBLE) ||
        // this.userGroups.includes(UserGroup.CUSTOMER_ACCOUNT_MANAGER)
      ) {
        return true;
      } else {
        return false;
      }
    },
    isViewOnlyUser() {
      return this.userGroups.includes(UserGroup.VIEW_ONLY);
    },
    titleStack() {
      return [
        { url: "#", name: "Data Management" },
        { url: "/datamangement/transaction", name: "Transactions" }
      ];
    }
  },
  mounted() {
    const switchCustomerData = this.fetchSwitchCustomerCache();
    if (switchCustomerData != null) {
      this.switchCustomerViewEnabled =
        switchCustomerData["isSwitchCustomerView"];
      this.switchCustomerId = switchCustomerData["switchCustomerNumber"];
    }
    if (this.mappedCustomer && this.mappedCustomer.length > 0) {
      this.switchCustomerViewEnabled = true;
      this.getCustomerById();
    } else {
      this.fetchCustomerData();
    }
    if (this.$route.params.customerId || this.switchCustomerId) {
      this.getCustomerById();
    }
  },

  methods: {
    manualMatchingCancel() {
      this.isManualMatchingModal = false;
    },
    saveBulkComment(data) {
      this.isBulkCommentModal = false;
      this.CDFTransactionDataService.saveBulkComment(
        this.customer.id,
        this.checkedData.map(records => records.id),
        this.query,
        this.isCheckedAll,
        data
      ).subscribe(
        () => {
          this.checkedData = [];
          this.map1 = new Map();
          this.checkedRow = [];
          this.isCheckedAll = false;
          this.checkedDataMap = new Map();
          // Reset table data before fetching
          this.tableData = [];
          this.currentPage = 0;
          this.nextPageCursor = null;
          this.nextData = false;
          this.previousData = false;
          this.$buefy.dialog.alert({
            title: "Success",
            message: "Comment(s) Created!",
            onConfirm: () => this.getCustomerById()
          });
        },
        e => {
          this.handleApiError(e);
          this.isLoading = false;
          console.log(e);
        }
      );
    },
    cancelBulkComment() {
      this.isBulkCommentModal = false;
    },
    BulkComments() {
      if (this.checkedData.length > 0) {
        this.isBulkCommentModal = true;
      } else {
        this.$buefy.dialog.alert({
          title: "Comment is Not Possible",
          message: "Please select at least one record"
        });
      }
    },
    viewConfirmExport() {
      this.checkedData = [];
      this.map1 = new Map();
      this.checkedRow = [];
      this.isBulkExportModal = false;
    },
    viewCancel() {
      this.checkedData = [];
      this.map1 = new Map();
      this.checkedRow = [];
      this.isBulkExportModal = false;
    },
    additionalBulkCheck(bulkAction) {
      let msg = "";
      if (this.totalRecords < this.exportLargeCount) {
        msg =
          `Are you sure you want to export ` +
          (this?.isCheckedAll
            ? `all`
            : `selected ` + this?.checkedData?.length) +
          ` records?`;
      } else {
        msg = `Are you sure you want to export more than 65000 records? Exported file will be sent to your email address`;
      }

      if (this.checkedData.length > 50 || this.isCheckedAll) {
        this.$buefy.dialog.confirm({
          title: "Confirmation",
          confirmText: "Yes",
          message: msg,
          trapFocus: true,
          onConfirm: value => this.exportAllTransactions(value, bulkAction)
        });
      } else {
        this.functionCallingMethod(false, bulkAction);
      }
    },
    exportTransactions() {
      try {
        this.CDFTransactionDataService.exportTransactions(
          this.customer.id,
          "Transactions",
          this.checkedData.map(records => records.id)
        );
      } catch (e) {
        this.$buefy.dialog.alert({
          title: "Export is Not Possible",
          message: e.message
        });
      }
      this.checkedData = [];
      this.map1 = new Map();
      this.checkedRow = [];
      this.isCheckedAll = false;
    },
    async exportAllTransactions() {
      this.isLoading = true;
      try {
        this.CDFTransactionDataService.exportAllTransactions(
          this.customer.id,
          "Transactions",
          this.query,
          this.totalRecords
        ).subscribe(
          () => {
            this.isLoading = false;
          },
          e => {
            this.isLoading = false;
            this.handleApiError(e);
            console.log(e);
          }
        );
      } catch (e) {
        this.$buefy.dialog.alert({
          title: "Error",
          message: e.message
        });
      }
      this.checkedData = [];
      this.map1 = new Map();
      this.checkedRow = [];
      this.isCheckedAll = false;
    },
    functionCallingMethod(value, bulkAction) {
      this.isCheckedAll = false;
      switch (bulkAction) {
        case "exportTransactions":
          this.exportTransactions();
          break;
      }
    },
    handleDataMatched() {
      // Reset table data before fetching
      this.tableData = [];
      this.currentPage = 0;
      this.nextPageCursor = null;
      this.nextData = false;
      this.previousData = false;
      this.checkedData = [];
      this.map1 = new Map();
      this.checkedRow = [];
      this.isCheckedAll = false;
      this.checkedDataMap = new Map();
      // Refresh the main transaction table immediately
      this.fetchTransactionData(this.query);
      // Show success message toast instead of modal dialog to avoid interruption
      this.$buefy.toast.open({
        message: "Data Matched Successfully!",
        type: "is-success",
        duration: 3000
      });
    },

    handleUnmatch() {
      try {
        if (!this.checkedData.length) {
          this.$buefy.dialog.alert({
            title: "Unmatch is Not Possible",
            message: "Please select at least one record to unmatch."
          });
          return;
        }
        this.CDFTransactionDataService.manualUnmatch(
          this.customer.id,
          this.checkedData.map(record => record.id)
        ).subscribe(
          () => {
            this.$buefy.dialog.alert({
              title: "Success",
              message: "Selected entries have been unmatched!"
            });
            this.checkedData = [];
            this.map1 = new Map();
            this.checkedRow = [];
            this.isCheckedAll = false;
            this.checkedDataMap = new Map();
            // Reset table data before fetching
            this.tableData = [];
            this.currentPage = 0;
            this.nextPageCursor = null;
            this.nextData = false;
            this.previousData = false;
            this.fetchTransactionData(this.query);
          },
          e => {
            this.handleApiError(e);
          }
        );
      } catch (e) {
        this.$buefy.dialog.alert({
          title: "Unmatch is Not Possible",
          message: e.message
        });
      }
    },

    saveMatchedData(data) {
      this.CDFTransactionDataService.saveMatchedData(
        this.customer.id,
        data
      ).subscribe(
        () => {
          // Wait for backend to complete, then trigger refresh
          this.$buefy.toast.open({
            message: "Match completed successfully!",
            type: "is-success",
            duration: 2000
          });

          // Trigger refresh in the ManualMatching modal's child tables
          if (this.$refs.manualMatchingModal) {
            this.$refs.manualMatchingModal.triggerRefresh();
          }
        },
        e => {
          this.handleApiError(e);
        }
      );
    },
    getCustomerById() {
      let customerId = this.$route.params.customerId
        ? this.$route.params.customerId
        : this.switchCustomerId;
      if (this.mappedCustomer.length > 0) {
        customerId = this.mappedCustomer[0];
      }
      this.customerDataService.getCustomerById(customerId).subscribe(
        response => {
          this.customer = response;
          this.changeCustomer();
        },
        e => {
          this.handleApiError(e);
          console.log(e);
        }
      );
    },
    pullNextPage() {
      this.previousData = true;
      this.loadMoreClicked = true;
      this.currentPage = this.currentPage + 1;
      const temp = this.nextPageCursor.split("?")[1].split("&");
      const query_part = temp.filter(function(value) {
        return value.indexOf("page=") == -1;
      });
      //query_part = [...new Set(query_part)];
      if (this.nextPageCursor != null || this.nextPageCursor != undefined) {
        this.query =
          "&" + query_part.join("&") + "&page=" + (this.currentPage + 1) + "&";
        //this.query = "&" + this.query;
        this.fetchTransactionData(this.query, "next");
      }
    },
    pullPreviousPage() {
      this.nextData = true;
      this.loadMoreClicked = true;
      const temp = this.PreviousPageCursor.split("?")[1].split("&");
      const query_part = temp.filter(function(value) {
        return value.indexOf("page=") == -1;
      });
      if (this.currentPage == 0 || this.currentPage == 1) {
        this.query = "&" + query_part.join("&") + "&page=1&";
        this.previousData = false;
      } else {
        this.query =
          "&" + query_part.join("&") + "&page=" + this.currentPage + "&";
      }
      this.currentPage =
        this.currentPage > 0 ? this.currentPage - 1 : this.currentPage;
      this.fetchTransactionData(this.query, "prev");
    },
    handleApiError(reason) {
      let errorMessage = null;
      if (reason?.response?.data) {
        if (reason.response.data["detail"]) {
          errorMessage = reason.response.data["detail"];
        } else if (reason.response.data["error"]) {
          errorMessage = reason.response.data["error"];
        } else if (reason.response.data["name"]) {
          errorMessage = reason.response.data["name"];
        } else {
          errorMessage = "Some issue while updating status";
        }
      }
      this.isLoading = false;
      this.$buefy.dialog.alert({
        title: "Error",
        message: errorMessage,
        type: "is-danger",
        ariaRole: "alertdialog",
        ariaModal: true
      });
    },
    checkedAllData(value) {
      this.isCheckedAll = value;
      if (!this.isCheckedAll) {
        this.checkedRow = [];
        this.map1 = new Map();
        this.checkedDataMap = new Map();
        this.checkedData = [];
      } else {
        this.checkedDataMap.set(this.currentPage, this.tableData);
      }
    },
    checkCheckBox(d) {
      // select all should be false now
      const total_checked = this.checkedData.length;
      const indexes = [];
      //getting the index of the checked rows
      d.forEach(element => {
        const index = this.tableData.findIndex(object => {
          return element.id === object.id;
        });
        indexes.push(index);
      });
      // create 2 dictionary. one with indexes for showing current page selected another with checked row value
      this.map1.set(this.currentPage, indexes);

      if (d.length > 0) {
        this.checkedDataMap.set(this.currentPage, d);
      } else {
        !this.isCheckedAll && this.checkedDataMap.set(this.currentPage, d);
      }
      this.checkedRow = d;
      this.checkedData = [];
      // converting mapped data into array for bulk operations
      this.checkedDataMap.forEach(element => {
        this.checkedData.push(...element);
      });
      // if checked all is true and deselect any
      if (this.isCheckedAll && total_checked > this.checkedData.length) {
        this.isCheckedAll = false;
        this.checkedDataMap = new Map();
        this.checkedData = [];
        this.checkedRow = [];
        this.map1 = new Map();
      }
    },
    searchByFilter(filterQuery) {
      this.loadMoreClicked = false;
      this.nextPageCursor = null;
      this.currentPage = 0;
      this.tableData = [];
      this.map1 = new Map();
      this.checkedRow = [];
      this.isCheckedAll = false;
      this.checkedData = [];
      this.checkedDataMap = new Map();
      this.nextData = false;
      this.query = "&" + filterQuery;
      //      this.displayedRecords = 0;

      let query = "";
      // Maintaining sort column & order in separate varaibles instead of this.query as it becomes difficult to replace & maintain
      // sort column & order part of this.query string. We should maintain query params in associative array instead of string
      if (this.lastSortColumn) {
        query += "&order_field" + "=" + this.lastSortColumn + "&";
        query += "&order_by" + "=" + this.lastSortOrder + "&";
        query += this.query;
      } else {
        query = this.query;
      }
      this.fetchTransactionData(query);
    },
    sortData(field, order) {
      this.loadMoreClicked = false;
      this.nextPageCursor = null;
      this.currentPage = 0;
      this.tableData = [];
      this.nextData = false;
      // sort should always fetch page 1, so removing page param if exist
      this.query = this.query.replace(/page=[0-9.]{1,2}&/g, "");

      const oldOrderField = "&order_field" + "=" + this.lastSortColumn + "&";
      const oldOrderValue = "&order_by" + "=" + this.lastSortOrder + "&";
      const newOrderField = "&order_field" + "=" + field + "&";
      const newOrderValue = "&order_by" + "=" + order + "&";

      this.query = this.replaceOrAddParanInSearchQuery(
        oldOrderField,
        newOrderField
      );
      this.query = this.replaceOrAddParanInSearchQuery(
        oldOrderValue,
        newOrderValue
      );

      //     this.displayedRecords = 0;
      this.lastSortColumn = field;
      this.lastSortOrder = order;
      this.fetchTransactionData(this.query);
    },

    replaceOrAddParanInSearchQuery(oldParam, newParam) {
      if (this.query.includes(oldParam)) {
        this.query = this.query.replace(oldParam, newParam);
      } else {
        this.query += newParam;
      }
      return this.query;
    },
    changeCustomer(Data) {
      this.columns = [];
      this.tableData = [];
      this.totalRecords = 0;
      this.loadMoreClicked = false;
      this.nextPageCursor = null;
      this.currentPage = 0;
      this.map1 = new Map();
      this.checkedRow = [];
      this.isCheckedAll = false;
      this.checkedData = [];
      this.customerEntity = this.customer?.landbell_entity || null;
      this.checkedDataMap = new Map();
      this.nextData = false;
      if (this.customer?.id) {
        this.fetchCDFTrasactionData(this.customer.id);
      }
    },

    saveFieldConfig() {
      // Get visible column fields in array format
      const visibleFields = this.columns
        .filter(column => column.visible && column.field !== "actions")
        .map(column => column.field);

      // Create a storage key that includes URL parameter (customerId), user ID
      const customerId = this.$route.params.customerId || this.customer.id;
      const storageKey = `transactions-field-config-${this.userId}-customer-${customerId}`;

      // Save to localStorage
      localStorage.setItem(storageKey, JSON.stringify(visibleFields));

      this.$forceUpdate();

      // After the DOM update, recalculate the table height to fill available space
      this.$nextTick(() => {
        this.adjustHeight();
      });
    },

    toggleSelectAll() {
      this.columns.forEach(column => {
        if (column.field !== "actions") {
          column.visible = this.selectAll;
        }
      });
      this.columnsForConfig = [...this.columns];
      this.saveFieldConfig();
    },

    prepareColumns(data) {
      // dynamically generate columns based on customer and stream
      const cdf_columns = data[0]["template_fields"];
      this.columns = [];
      this.tableData = [];

      // Create a storage key that includes URL parameter (customerId), user ID
      const customerId = this.$route.params.customerId || this.customer.id;
      const storageKey = `transactions-field-config-${this.userId}-customer-${customerId}`;

      // Try to load from user-specific storage first
      let savedConfig = localStorage.getItem(storageKey);

      // If no user-specific config exists, try other formats for backward compatibility
      if (!savedConfig) {
        // Try the old format with just customer ID
        savedConfig = localStorage.getItem(
          `transactions-field-config-${customerId}`
        );
      }

      // Parse the saved configuration
      const savedFieldConfig = savedConfig ? JSON.parse(savedConfig) : null;
      this.columns.push({
        field: "customer_name",
        label: "Customer",
        searchable: true,
        filterKey: { customer_name: "" },
        type: "input",
        width: "200",
        visible: savedFieldConfig
          ? Array.isArray(savedFieldConfig)
            ? savedFieldConfig.includes("customer_name")
            : savedFieldConfig["customer_name"] !== false
          : true // Default to visible when no saved config
      });
      cdf_columns.forEach(e => {
        if (e.field_required == false) {
          return;
        }
        let type = "";
        let multiSelectFilter = "false";
        type = this.getFieldType(e.field_type);
        const filterKey = {};
        const allStatusVals = [];
        const editableCheck = e?.editable_check ? e.editable_check : false;
        const field_name = e.transaction_data_column;
        const label_name =
          e.field_name.charAt(0).toUpperCase() +
          e.field_name.slice(1).toLowerCase();
        if (type == "multiSelectFilter") {
          multiSelectFilter = "true";
          filterKey[field_name] = [];
          filterKey["field_name"] = "name";
        } else {
          filterKey[field_name] = "";
        }
        if (e.dropdown_field_values?.length > 0) {
          e.dropdown_field_values.forEach(element => {
            allStatusVals.push({ name: element });
          });
        }
        this.columns.push({
          field: field_name,
          label: label_name,
          searchable: true,
          filterKey: filterKey,
          type: type,
          multiSelectFilter: multiSelectFilter,
          initStatusVals: [],
          allStatusVals: allStatusVals,
          editableCheck: editableCheck,
          width: "300",
          // Default to visible (true) if no saved config
          visible: savedFieldConfig
            ? Array.isArray(savedFieldConfig)
              ? savedFieldConfig.includes(field_name)
              : savedFieldConfig[field_name] !== false
            : true
        });
      });
      this.columns.sort((a, b) => a?.template_field - b?.template_field);
      this.columns.push({
        field: "common_product",
        label: "Matched Product Number",
        searchable: true,
        filterKey: { common_product: "" },
        type: "input",
        width: "150",
        // Default to visible (true) if no saved config
        visible: savedFieldConfig
          ? Array.isArray(savedFieldConfig)
            ? savedFieldConfig.includes("common_product")
            : savedFieldConfig["common_product"] !== false
          : true
      });

      this.columns.push({
        field: "common_product_name",
        label: "Matched Product Name",
        searchable: true,
        filterKey: { common_product_name: "" },
        type: "input",
        width: "150",
        // Default to visible (true) if no saved config
        visible: savedFieldConfig
          ? Array.isArray(savedFieldConfig)
            ? savedFieldConfig.includes("common_product")
            : savedFieldConfig["common_product"] !== false
          : true
      });
      this.columns.push({
        field: "match_type",
        label: "Match Type",
        editableCheck: true,
        searchable: true,
        filterKey: {
          match_type: [],
          field_name: "name"
        },
        type: "multiSelectFilter",
        multiSelectFilter: true,
        allStatusVals: [
          { name: "Exact" },
          { name: "Like for Like" },
          { name: "Comparable" },
          { name: "NA" }
        ],
        width: "200",
        visible: savedFieldConfig
          ? Array.isArray(savedFieldConfig)
            ? savedFieldConfig.includes("match_type")
            : savedFieldConfig["match_type"] !== false
          : true
      });
      this.columns.push({
        field: "period_of_import",
        label: "Period",
        searchable: true,
        filterKey: { period_of_import: "" },
        type: "input",
        width: "300",
        // Default to visible (true) if no saved config
        visible: savedFieldConfig
          ? Array.isArray(savedFieldConfig)
            ? savedFieldConfig.includes("period_of_import")
            : savedFieldConfig["period_of_import"] !== false
          : true
      });
      this.columns.push({
        field: "comment",
        label: "External User Comment",
        searchable: true,
        additionalInfo: true,
        additionalInfoIcon: "mdi  mdi-information",
        additionalInfoFieldName: "comment",
        filterKey: { comment: "" },
        type: "input",
        width: "400",
        // Default to visible (true) if no saved config
        visible: savedFieldConfig
          ? Array.isArray(savedFieldConfig)
            ? savedFieldConfig.includes("comment")
            : savedFieldConfig["comment"] !== false
          : true
      });
      this.columns.push({
        field: "created_at",
        label: "Imported On",
        searchable: true,
        filterKey: { created_at: "" },
        type: "dateField",
        width: "300",
        // Default to visible (true) if no saved config
        visible: savedFieldConfig
          ? Array.isArray(savedFieldConfig)
            ? savedFieldConfig.includes("created_at")
            : savedFieldConfig["created_at"] !== false
          : true
      });
      this.columns.push({
        field: "created_by_name",
        label: "Created By",
        searchable: true,
        filterKey: { created_by_name: "" },
        type: "input",
        width: "300",
        // Default to visible (true) if no saved config
        visible: savedFieldConfig
          ? Array.isArray(savedFieldConfig)
            ? savedFieldConfig.includes("created_by_name")
            : savedFieldConfig["created_by_name"] !== false
          : true
      });

      this.columns.push({
        field: "updated_by_name",
        label: "Updated By",
        searchable: true,
        filterKey: { updated_by_name: "" },
        type: "input",
        width: "300",
        // Default to visible (true) if no saved config
        visible: savedFieldConfig
          ? Array.isArray(savedFieldConfig)
            ? savedFieldConfig.includes("updated_by_name")
            : savedFieldConfig["updated_by_name"] !== false
          : true
      });
      this.columns.push({
        field: "actions",
        label: "",
        sortable: 1,
        headerClass: "is-actions",
        cellClass: "is-actions",
        visible: true,
        searchable: true,
        type: "actionSearch",
        actionSearch: true,
        filterKey: { actions: "" }
      });
      // Update columnsForConfig for the dropdown
      this.columnsForConfig = [...this.columns];

      // Initialize selectAll based on current column visibility
      this.selectAll = this.columns.every(
        column => column.field === "actions" || column.visible !== false
      );

      this.fetchTransactionData();
    },

    fetchCDFTrasactionData(customer_id) {
      this.isDataLoading = true;
      //here sending true for geting NA values for dropdowns
      this.CDFTransactionDataService.getCdfTransactionField(
        customer_id,
        true
      ).subscribe(
        response => {
          this.cdf_transaction_sheet = response;
          if (response.length > 0) {
            this.prepareColumns(response);
          }
          this.isDataLoading = false;
        },
        e => {
          console.log(e);
          this.handleApiError(e);
          this.isDataLoading = false;
        }
      );
    },
    fetchCustomerData() {
      this.isDataLoading = true;
      this.customerEntity = "";
      this.customerDataService.getParentCustomerList().subscribe(
        response => {
          const data = response.sort((a, b) => (a.name > b.name ? 1 : -1));
          this.customers = data;
          this.isDataLoading = false;
        },
        e => {
          this.handleApiError(e);
          console.log(e);
        }
      );
    },
    routeToImportForm() {
      this.$router.push({
        name: "datamanagement.importform",
        params: {
          customer: this.customer,
          importType: { id: "2", name: "Transactions" }
        }
      });
    },

    prepareData(data) {
      const dataList = [];
      for (const report in data) {
        const reportData = data[report];
        reportData["updated_at"] = this.dateToLocalDate(
          reportData["updated_at"]
        );
        reportData["created_at"] = dayjs(reportData["created_at"]).format(
          "YYYY-MM-DD, HH:mm"
        );
        reportData["actions"] = "true";
        dataList.push(reportData);
      }

      if (this.nextPageCursor == null) {
        this.tableData.push(...dataList);
        if (this.currentPage > 1) {
          this.currentPage = this.currentPage - 1;
          if (this.currentPage == 1) {
            this.previousData = false;
          }
        } else {
          this.currentPage = this.currentPage - 1;
          this.previousData = false;
        }
      } else {
        this.tableData = dataList;
      }
      //if select all checkbox clicked the show all the data for the page as checked
      if (this.isCheckedAll) {
        this.checkedRow = this.tableData;
      } else {
        //else show the corresponding data from the dictionay map1
        const temp = [];
        // check if dictionary has value or not
        if (
          this.map1.get(this.currentPage) &&
          this.map1.get(this.currentPage).length > 0
        ) {
          //inserting data according to index of the current page
          this.map1.get(this.currentPage).forEach(element => {
            temp.push(this.tableData[element]);
          });
          this.checkedRow = temp;
        } else {
          this.checkedRow = [];
        }
      }
      this.checkSize();
      this.adjustHeight();
      this.isDataLoading = false;
    },
    fetchTransactionData(query = "", scrollDir) {
      this.isDataLoading = true;
      this.DataManagementService.getTransactionData(
        this.customer.id,
        query
      ).subscribe(
        response => {
          this.PreviousPageCursor = this.nextPageCursor
            ? this.nextPageCursor
            : this.PreviousPageCursor;
          this.nextPageCursor = response["next"];
          if (this.currentPage > 1) {
            this.previousData = true;
            if (this.nextPageCursor != null) {
              this.enableLoadMore = this.nextData = true;
            } else {
              this.enableLoadMore = this.nextData = false;
            }
          } else {
            this.enableLoadMore = this.previousData = false;
            if (this.nextPageCursor != null) {
              this.enableLoadMore = this.nextData = true;
            } else {
              this.enableLoadMore = this.nextData = false;
            }
          }
          this.totalRecords = response["count"];
          let tempData = [];
          if (scrollDir == "next") {
            //this.totalRecords = this.data.length + response["count"];
            if (!this.nextPageCursor) {
              this.prepareData(response["results"]);
            } else {
              tempData = [...this.tableData, ...response["results"]];
              if (tempData.length > 100 && this.nextPageCursor != null) {
                tempData = tempData.slice(50, 150);
              }
              this.prepareData(tempData);
            }
          } else if (scrollDir == "prev") {
            //this.totalRecords = this.data.length + response["count"];
            if (this.currentPage == 0) {
              this.prepareData(response["results"]);
            } else {
              tempData = [...response["results"], ...this.tableData];
              if (tempData.length >= 100) {
                tempData = tempData.slice(0, 100);
              }
              this.prepareData(tempData);
            }
          } else {
            this.prepareData(response["results"]);
          }
          this.isDataLoading = false;
        },
        e => {
          this.nextData = false;
          this.handleApiError(e);
          console.log(e);
          this.isDataLoading = false;
        }
      );
    },

    getFieldType(value) {
      switch (value) {
        case FieldType.TEXT:
          return "input";
        case FieldType.DATE:
          return "dateField";
        case FieldType.NUMBER:
          return "input";
        case FieldType.DROPDOWN:
          return "multiSelectFilter";
        case FieldType.NUMBERRANGE:
          return "numberRange";
        default:
          return "input";
      }
    },

    checkSize() {
      screenPixelSize = screen.width;
      pixelRatio =
        window.devicePixelRatio ||
        window.screen.availWidth / document.documentElement.clientWidth;
      pixelRatio = pixelRatio.toFixed(1);
      if (
        (screenPixelSize > 1024 &&
          screenPixelSize < 1281 &&
          pixelRatio == 1.5) ||
        (screenPixelSize > 1580 && screenPixelSize < 1921 && pixelRatio == 1.5)
      ) {
        this.subHeight = this.subHeight - 15;
      }
      this.$refs.circul8Table.$refs.multiselect &&
        this.$refs.circul8Table.$refs.multiselect.forEach(element => {
          element.deactivate();
        });
      this.$refs.circul8Table.$refs.dropdown &&
        this.$refs.circul8Table.$refs.dropdown.forEach(element => {
          element.isActive = false;
        });
      this.$refs.circul8Table.$refs.datePicker &&
        this.$refs.circul8Table.$refs.datePicker.forEach(element => {
          element.closePopup();
        });
    },
    adjustHeight() {
      const browser_height = window.innerHeight;
      const newTableHeight = browser_height - this.subHeight;
      document.querySelector(".table-wrapper").style.maxHeight =
        newTableHeight + "px";
      //console.log( document.querySelector(".table-wrapper").style, newTableHeight, "val");
      //this.tableHeight = newTableHeight + "px";
    }
  }
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
